import {HttpClient, POST, PUT, ServerValidationResult, expectEither, useHttpClient} from '@raison/network-facade';
import {AccountId, Outcome} from '@raison/types';
import {queryOptions} from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import {absurd} from 'fp-ts/lib/function';
import {CryptoCurrencyType} from '~/entities/currencies';
import {Wish} from '~/library/prelude';
import {TrTx} from '../model/TrTx';
import {CalculatedAmounts, FeeCalculationType} from '../ui/SendTxIntro';

export interface FeeRequestData {
    accountId: AccountId;
    accountType: CryptoCurrencyType;
    amount: BigNumber;
    address?: string;
    feeCalculationType: FeeCalculationType;
}

export namespace FeeRequestData {
    export function fromTrTx(trt: TrTx): FeeRequestData {
        return {
            accountId: trt.fromAccount.id,
            accountType: trt.fromAccount.type,
            address: trt.toAddress,
            amount: trt.amount,
            feeCalculationType: FeeCalculationType.OnTop,
        };
    }
}

const sharedExpectations = {
    200: Wish.struct({
        fee: Wish.BigNumberFromString,
        amount_send: Wish.BigNumberFromString,
        amount_receive: Wish.BigNumberFromString,
    }).pipe(
        Wish.morph(
            (data): CalculatedAmounts => ({
                feeAmount: data.fee,
                amountToBeSent: data.amount_send,
                amountToBeReceived: data.amount_receive,
            })
        ),
        Wish.toStandardSchemaV1
    ),
};

export async function fetchFeesOnSend(http: HttpClient, data: FeeRequestData, options?: {signal: AbortSignal}) {
    const [url, init] = prepareFeeOptionsRequest(data);
    return await http(url, {...init, ...options}).then(
        ...expectEither({
            ...sharedExpectations,
            400: ServerValidationResult.to({amount: 'string?', address: 'string?'}),
        })
    );
}

export function fetchTrOperationFees(http: HttpClient, data: FeeRequestData, options?: {signal: AbortSignal}) {
    const [url, init] = prepareFeeOptionsRequest(data);
    return http(url, {...init, ...options}).then(
        ...expectEither({
            ...sharedExpectations,
            400: ServerValidationResult.to({amount: 'string'}),
        })
    );
}

function prepareFeeOptionsRequest({accountId, accountType, ...data}: FeeRequestData) {
    const [init, url] = (() => {
        switch (accountType) {
            case 'crypto':
                return [PUT, `/v1/wallets/${accountId}/fee`];
            case 'stablecoin':
                return [POST, `/v1/tokens/${accountId}/fee`];
        }
    })();

    const body = {
        amount: data.amount.toString(),
        address: data.address,
        ...(accountType === 'stablecoin' ? {} : {fee_calculation_type: data.feeCalculationType}),
    };
    return [url, init(body)] as const;
}

export function useFeesOnSendOptions(data: FeeRequestData) {
    const http = useHttpClient();

    const {queryKey, queryFn, gcTime} = queryOptions({
        queryKey: ['feesOnSend', data] as const,
        queryFn: async ({
            queryKey,
            client,
        }): Promise<Outcome<CalculatedAmounts, {amount?: string; address?: string}>> => {
            const [, data] = queryKey;
            const response = await fetchFeesOnSend(http, data);
            switch (response.status) {
                case 400:
                    return {problem: response.json};
                case 200: {
                    return {value: response.json};
                }
                case 'Abort':
                    await client.cancelQueries({queryKey});
                    throw response.reason;
                default:
                    return absurd<never>(response);
            }
        },
        gcTime: 1000 * 10,
    });

    return {queryKey, queryFn: queryFn!, gcTime: gcTime!};
}
